<template>
  <div class="autocomplete-container">
    <v-text-field
      v-model="searchInput"
      @input="handleInput"
      :label="label"
      :rules="rules"
      :append-icon="required ? 'mdi-asterisk' : ''"
      class="mb-2"
    />

    <v-list v-if="predictions.length" class="predictions-list pa-0 elevation-4">
      <v-list-item
        v-for="(prediction, index) in predictions"
        :key="index"
        @click="selectPrediction(prediction)"
        class="cursor-pointer"
      >
        <v-list-item-content>
          <v-list-item-title>
            {{ prediction.description }}
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </v-list>
  </div>
</template>

<script>
export default {
  name: 'AddressAutocomplete',
  props: {
    label: {
      required: true,
      type: String,
    },
    required: {
      type: Boolean,
      default: false
    },
    rules: {
      type: Array,
      default: () => []
    },
    value: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      apiReady: false,
      apiKey: process.env.VUE_APP_GOOGLE_API_KEY,
      predictions: [],
      searchInput: '',
      geocoder: null,
      inputTimeout: null
    };
  },
  mounted() {
    this.loadGoogleMapsAPI();
  },
  beforeDestroy() {
    if (this.inputTimeout) {
      clearTimeout(this.inputTimeout);
    }
  },
  methods: {
    async handleInput(value) {
      if (!value || value.length < 3) {
        this.predictions = [];
        return;
      }

      if (!this.apiKey) {
        console.warn('VUE_APP_GOOGLE_API_KEY is not set');
        return;
      }

      if (!this.apiReady) {
        console.warn('Google Maps API is still loading...');
        return;
      }

      clearTimeout(this.inputTimeout);
      this.inputTimeout = setTimeout(() => {
        try {
          if (window.google && window.google.maps && window.google.maps.places) {
            const service = new window.google.maps.places.AutocompleteService();
            service.getPlacePredictions({ input: value }, (predictions, status) => {
              if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                this.predictions = predictions || [];
              } else {
                console.error('Error fetching predictions status:', status);
                this.predictions = [];
              }
            });
          }
        } catch (error) {
          console.error('Error fetching predictions:', error);
          this.predictions = [];
        }
      }, 300);
    },

    initializeGoogleMaps() {
      if (window.google && window.google.maps) {
        this.geocoder = new window.google.maps.Geocoder();
        this.apiReady = true;
      }
    },

    loadGoogleMapsAPI() {
      if (!this.apiKey) {
        console.warn('VUE_APP_GOOGLE_API_KEY is not set in environment variables');
        return;
      }

      if (window.google && window.google.maps) {
        this.initializeGoogleMaps();
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        this.initializeGoogleMaps();
      };
      script.onerror = () => {
        console.error('Failed to load Google Maps API');
      };
      document.head.appendChild(script);
    },

    getAddressComponent(result, type, useShortName = false) {
      if (!result || !result.address_components) return '';
      const component = result.address_components.find((c) => c.types.includes(type));
      if (!component) return '';
      return useShortName ? component.short_name : component.long_name;
    },

    async selectPrediction(prediction) {
      // this.searchInput = prediction.description;
      this.predictions = [];

      try {
        if (this.geocoder) {
          const result = await new Promise((resolve, reject) => {
            this.geocoder.geocode({ address: prediction.description }, (results, status) => {
              if (status === 'OK') {
                resolve(results);
              } else {
                reject(new Error(`Geocoding failed: ${status}`));
              }
            });
          });

          if (result && result.length > 0) {
            const streetNumber = this.getAddressComponent(result[0], 'street_number');
            const streetName = this.getAddressComponent(result[0], 'route');
            const city = this.getAddressComponent(result[0], 'locality');
            const state = this.getAddressComponent(result[0], 'administrative_area_level_1', true);
            const zip = this.getAddressComponent(result[0], 'postal_code');

            // Extraction of Address Line 2 (Apartment / Suite / Unit)
            let addressLine2 = '';
            const subpremise = this.getAddressComponent(result[0], 'subpremise');

            // Try to find subpremise in geocode components
            if (subpremise) {
              addressLine2 = subpremise;
            }

            // Fallback 1: Extract from the first segment of the prediction description
            const descriptionParts = prediction.description.split(',').map((p) => p.trim());
            const firstPart = descriptionParts[0] || '';
            const addressLine1 = `${streetNumber} ${streetName}`.trim();

            if (!addressLine2 && firstPart.toLowerCase().startsWith(addressLine1.toLowerCase()) && firstPart.length > addressLine1.length) {
              const extra = firstPart.substring(addressLine1.length).trim();
              addressLine2 = extra.replace(/^[,#\s]+/, '').trim();
            }

            // Fallback 2: Check if any other segment in the description is an apartment, suite, unit, etc.
            if (!addressLine2 && descriptionParts.length > 1) {
              const aptKeywords = /^(apt|apartment|suite|ste|unit|bldg|building|floor|fl|dept|department|#|room|rm)\b/i;
              // Look through non-first, non-last segments (city/state usually at the end)
              for (let i = 1; i < descriptionParts.length - 1; i++) {
                const segment = descriptionParts[i] || '';
                const cleanSegment = segment.toLowerCase();

                // Skip any segment that matches city, state, or zip to avoid false positives
                if (
                  (city && cleanSegment === city.toLowerCase()) ||
                  (state && cleanSegment.includes(state.toLowerCase())) ||
                  (zip && cleanSegment.includes(zip.toLowerCase()))
                ) {
                  continue;
                }

                if (aptKeywords.test(segment)) {
                  addressLine2 = segment;
                  break;
                }
              }
            }

            // Format addressLine2 neatly (e.g. if it's just a number, prefix with "Apt")
            if (addressLine2 && /^\d+[a-zA-Z]?$/.test(addressLine2)) {
              addressLine2 = `Apt ${addressLine2}`;
            }

            this.searchInput = addressLine1;
            this.$emit('address-selected', {
              addressLine1: addressLine1,
              addressLine2: addressLine2,
              city: city,
              state: state,
              zip: zip
            });
          }
        }
      } catch (error) {
        console.error('Error getting geocode:', error);
      }
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(val) {
        if (val !== this.searchInput) {
          this.searchInput = val;
        }
      }
    },
  },
}
</script>

<style scoped>
.autocomplete-container {
  position: relative;
  width: 100%;
}

.predictions-list {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 10;
  max-height: 250px;
  overflow-y: auto;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background-color: white !important;
}

.cursor-pointer {
  cursor: pointer;
}

.cursor-pointer:hover {
  background-color: #f5f5f5 !important;
}
</style>
